import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Box, CircularProgress, Alert, IconButton, Collapse, Typography, Paper } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PharmacyMapFilters from './PharmacyMapFilters';
import PharmacyMapTable from './PharmacyMapTable';
import MapView from './MapView';
import type { PharmacyLocation, CachedData, ProductSearchResult, UserContactInfo } from './types';
import { CACHE_KEY, CACHE_TTL, calculateDistance } from './utils';

/**
 * Главный компонент модуля Карты аптек
 * Интегрирует фильтры, карту и таблицу
 */
const PharmacyMap: React.FC = () => {
  const [locations, setLocations] = useState<PharmacyLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [radiusFilter, setRadiusFilter] = useState<number>(15);
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [userLocation, setUserLocation] = useState<PharmacyLocation | null>(null);
  const [isFetched, setIsFetched] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<[number, number] | null>(null);
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchType, setSearchType] = useState<'product' | 'pharmacy'>('product'); // По умолчанию "Товар"
  const [isMapExpanded, setIsMapExpanded] = useState<boolean>(true); // Карта развернута/свернута
  const [productSearchResults, setProductSearchResults] = useState<ProductSearchResult[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const [lastCacheUpdate, setLastCacheUpdate] = useState<Date | null>(null);
  const [userContacts, setUserContacts] = useState<Map<string, UserContactInfo>>(new Map());
  const [loadingContacts, setLoadingContacts] = useState<boolean>(true);

  // Обработчик показа на карте
  const handleShowOnMap = useCallback(() => {
    // 1. Раскрываем карту если свернута
    if (!isMapExpanded) {
      setIsMapExpanded(true);
    }

    // 2. Скроллим к карте с небольшой задержкой для раскрытия
    setTimeout(() => {
      mapSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, isMapExpanded ? 0 : 300);
  }, [isMapExpanded]);

  // Обработчик клика на строку таблицы
  const handleRowClick = useCallback((username: string, lat: number, lng: number) => {
    setSelectedLocation([lat, lng]);
    setSelectedUsername(username);

    // Показываем на карте
    handleShowOnMap();

    // Открываем popup на маркере
    setTimeout(() => {
      const marker = markersRef.current.get(username);
      if (marker) {
        marker.openPopup();
      }
    }, 300);

    console.log(`📍 Показ ${username} на карте`);
  }, [handleShowOnMap]);

  useEffect(() => {
    document.title = 'Карта аптек | Zerde';

    if (!isFetched) {
      fetchLocations();
    }

    // Загружаем статус кеша
    fetchCacheStatus();

    // Обновляем статус кеша каждые 5 минут
    const cacheStatusInterval = setInterval(fetchCacheStatus, 5 * 60 * 1000);

    return () => clearInterval(cacheStatusInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Загружаем контакты после того как локации получены
  useEffect(() => {
    if (!loading && locations.length > 0) {
      fetchUserContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, locations.length]);

  const fetchCacheStatus = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/nomenclature/stats');

      if (response.data.success && response.data.stats?.lastUpdated) {
        setLastCacheUpdate(new Date(response.data.stats.lastUpdated));
      }
    } catch (err) {
      console.error('Ошибка при получении статуса кэша:', err);
    }
  };

  const fetchLocations = async () => {
    // Проверяем кэш
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const cachedData: CachedData = JSON.parse(cached);
        const now = Date.now();

        if (now - cachedData.timestamp < CACHE_TTL) {
          console.log(' Загрузка данных из кэша');
          processLocations(cachedData.data);
          setLoading(false);
          setIsFetched(true);
          return;
        }
      } catch (e) {
        console.warn('Ошибка чтения кэша');
      }
    }

    try {
      setLoading(true);
      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/geo/all-locations');

      if (response.data.success) {
        const allLocations = response.data.locations;

        // Сохраняем в кэш
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
          data: allLocations,
          timestamp: Date.now()
        }));

        processLocations(allLocations);
        setError(null);
      } else {
        setError('Не удалось загрузить данные геолокаций');
      }
    } catch (err) {
      console.error('Ошибка при загрузке геолокаций:', err);
      setError('Ошибка при получении данных');
    } finally {
      setLoading(false);
      setIsFetched(true);
    }
  };

  const processLocations = (allLocations: PharmacyLocation[]) => {
    setLocations(allLocations);

    const username = localStorage.getItem('username');
    let userLoc: PharmacyLocation | undefined = undefined;

    if (username) {
      userLoc = allLocations.find((loc: PharmacyLocation) => loc.username?.toLowerCase() === username.toLowerCase());
      if (userLoc) {
        setUserLocation(userLoc);
        setMapCenter([userLoc.latitude, userLoc.longitude] as [number, number]);
      }
    }

    if (allLocations.length > 0 && !userLoc) {
      setMapCenter([allLocations[0].latitude, allLocations[0].longitude] as [number, number]);
    }
  };

  // Загрузка контактной информации пользователей
  const fetchUserContacts = useCallback(async () => {
    try {
      setLoadingContacts(true);
      const response = await axios.get('https://tbl.zerdegroup.kz/api/users');

      if (response.data && Array.isArray(response.data)) {
        // Преобразуем в Map для O(1) доступа (ключи в lowercase для поиска без учета регистра)
        const contactsMap = new Map(
          response.data
            .filter(user => user.userphone) // Только пользователи с телефонами
            .map(user => [
              user.username.toLowerCase(), // Ключ в нижнем регистре
              {
                phone: user.userphone,
                fullName: user.fullName || 'Не указано'
              }
            ])
        );

        setUserContacts(contactsMap);
        console.log(`📞 Загружено контактов: ${contactsMap.size}`);
      }
    } catch (error) {
      console.error('Ошибка при загрузке контактов:', error);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  // Поиск товаров с дебаунсингом
  const searchProducts = useCallback(async (query: string) => {
    if (!query || query.trim().length === 0) {
      setProductSearchResults([]);
      return;
    }

    try {
      setLoadingProducts(true);

      const params: any = { searchQuery: query };

      // Если есть радиус и локация пользователя, добавляем в запрос
      if (radiusFilter > 0 && userLocation) {
        params.radiusKm = radiusFilter;
        params.userLat = userLocation.latitude;
        params.userLng = userLocation.longitude;
      }

      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/geo/search-products', {
        params
      });

      if (response.data.success) {
        setProductSearchResults(response.data.results || []);
        console.log(` Найдено аптек с товаром "${query}": ${response.data.results.length}`);
      }
    } catch (error) {
      console.error('Ошибка при поиске товаров:', error);
      setProductSearchResults([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [radiusFilter, userLocation]);

  // useEffect для дебаунсинга поиска товаров
  useEffect(() => {
    if (searchType === 'product' && searchQuery.trim().length > 0) {
      // Очищаем предыдущий таймер
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Сразу устанавливаем статус загрузки перед debounce
      setLoadingProducts(true);

      // Устанавливаем новый таймер (500ms)
      debounceTimerRef.current = setTimeout(() => {
        searchProducts(searchQuery);
      }, 500);
    } else if (searchType === 'product' && searchQuery.trim().length === 0) {
      setProductSearchResults([]);
      setLoadingProducts(false);
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, searchType, searchProducts]);

  // Фильтрация по поиску + радиусу
  const filteredLocations = useMemo(() => {
    let filtered = locations;

    // Поиск для типа "Аптека"
    if (searchQuery.trim() && searchType === 'pharmacy') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((loc) =>
        loc.username.toLowerCase().includes(query) ||
        (loc.address && loc.address.toLowerCase().includes(query))
      );
    }

    // Для типа "Товар" - фильтрация по результатам поиска
    if (searchQuery.trim() && searchType === 'product') {
      if (productSearchResults.length > 0) {
        // Есть результаты - показываем только их
        const pharmacyUsernames = new Set(productSearchResults.map(r => r.phNumber?.toLowerCase()).filter(Boolean));
        filtered = filtered.filter((loc) => pharmacyUsernames.has(loc.username?.toLowerCase()));
      } else if (!loadingProducts) {
        // Поиск завершен, но результатов нет - показываем пустой список
        filtered = [];
      }
      // Если loadingProducts === true, оставляем предыдущие результаты (не фильтруем)
    }

    // Фильтрация по радиусу (не отсекает результаты поиска конкретной аптеки по названию/номеру)
    const shouldApplyRadius = radiusFilter > 0 && userLocation && !(searchType === 'pharmacy' && searchQuery.trim());
    if (shouldApplyRadius) {
      filtered = filtered.filter((loc) => {
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          loc.latitude,
          loc.longitude
        );
        return distance <= radiusFilter;
      });
    }

    return filtered;
  }, [locations, radiusFilter, userLocation, searchQuery, searchType, productSearchResults, loadingProducts]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (locations.length === 0) {
    return <Alert severity="info">Пока нет данных о геолокациях аптек</Alert>;
  }

  return (
    <Box >

      {/* Фильтры и поиск */}
      <PharmacyMapFilters
        searchQuery={searchQuery}
        searchType={searchType}
        radiusFilter={radiusFilter}
        userLocation={userLocation}
        filteredCount={filteredLocations.length}
        totalCount={locations.length}
        loadingProducts={loadingProducts}
        lastCacheUpdate={lastCacheUpdate}
        onSearchChange={setSearchQuery}
        onSearchTypeChange={setSearchType}
        onRadiusChange={setRadiusFilter}
      />

      {/* Таблица - сверху */}
      <PharmacyMapTable
        locations={filteredLocations}
        userLocation={userLocation}
        onRowClick={handleRowClick}
        searchType={searchType}
        searchQuery={searchQuery}
        productSearchResults={productSearchResults}
        isSearching={loadingProducts}
        onShowOnMap={handleShowOnMap}
        userContacts={userContacts}
        loadingContacts={loadingContacts}
      />

      {/* Карта - внизу */}
      <Paper
        ref={mapSectionRef}
        elevation={0}
        sx={{
          mt: 3,
          p: 2,
          borderRadius: '12px',
          scrollMarginTop: '20px',
          border: '1px solid #e4e4e7',
          boxShadow: 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem', color: '#09090b', letterSpacing: '-0.02em' }}>
            Карта
          </Typography>
          <IconButton
            size="small"
            onClick={() => setIsMapExpanded(!isMapExpanded)}
            sx={{ ml: 'auto', color: '#71717a', '&:hover': { color: '#09090b', bgcolor: '#e4e4e7' } }}
          >
            {isMapExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Карта с анимацией сворачивания */}
        <Collapse in={isMapExpanded} timeout={300}>
          <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e4e4e7' }}>
            <MapView
              locations={filteredLocations}
              mapCenter={mapCenter}
              selectedLocation={selectedLocation}
              userLocation={userLocation}
              radiusFilter={radiusFilter}
              markersRef={markersRef}
            />
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default PharmacyMap;

